/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';

/** 
 * Constants: Platform total minutes per day (billions).
 * These are now just default values, actual values will be in state
 */
const DEFAULT_TT_MIN_PER_DAY = 7.5;
const DEFAULT_IG_MIN_PER_DAY = 4.0;
const DEFAULT_YT_MIN_PER_DAY = 3.0;

/**
 * ----------------------------
 * 1) Type Definitions
 * ----------------------------
 */
interface CalculatorInputs {
  // Platform Minutes
  tt_minutes_per_day: number;
  ig_minutes_per_day: number;
  yt_minutes_per_day: number;

  // Non-Follower Ratios (Pre-Ban)
  tt_non_follower_ratio_pre: number;
  ig_non_follower_ratio_pre: number;
  yt_non_follower_ratio_pre: number;

  // Non-Follower Ratios (Post-Ban)
  ig_non_follower_ratio_post: number;
  yt_non_follower_ratio_post: number;

  // Conversion Rates
  tt_conversion_rate: number;
  ig_conversion_rate: number;
  yt_conversion_rate: number;

  // Spillover & Ads
  ad_siphon: number;  // fraction lost to ads
  ig_share_of_spillover: number;
  yt_share_of_spillover: number;
  other_share_of_spillover: number;
}

interface CalculatorResults {
  // Non-Follower Attention Totals
  preBanNonFollowerTotal: number;
  postBanNonFollowerTotal: number;
  nonFollowerAttentionChangePct: number;

  // Revenue Totals
  preBanRevenue: number;
  postBanRevenue: number;
  revenueChangePct: number;
}

/**
 * ----------------------------
 * 2) Common CSS Classes
 * ----------------------------
 */
const colors = {
  bg: "bg-[#0F172A]", // Dark blue background
  section: "bg-[#1E293B]", // Slightly lighter blue for sections
  input: "bg-[#334155]", // Input background
  text: "text-[#E2E8F0]", // Light gray text
  accent: "text-[#38BDF8]", // Bright blue accent
  muted: "text-[#94A3B8]", // Muted text
  success: "text-[#4ADE80]", // Green for success
  error: "text-[#FB7185]", // Red for error
  border: "border-[#334155]", // Border color
  hover: "hover:bg-[#475569]", // Hover state
  focus: "focus:ring-[#38BDF8]", // Focus ring color
};

const sectionClass = `${colors.section} rounded-xl shadow-xl p-8 mb-6 border ${colors.border} backdrop-blur-sm backdrop-filter`;
const inputClass = `
  mt-1 w-24 rounded-lg
  ${colors.input} border ${colors.border}
  ${colors.text} placeholder-${colors.muted}
  focus:border-[#38BDF8] focus:ring-1 focus:ring-[#38BDF8]
  text-sm px-2 py-1.5
`;
const helperTextClass = `mt-1 text-sm ${colors.muted}`;

/**
 * Sliders: We apply custom styles for the slider "thumb" and track.
 */
const sliderClass = `
  w-48 h-2 rounded-lg appearance-none cursor-pointer
  bg-gradient-to-r from-[#0EA5E9] to-[#38BDF8]
  [&::-webkit-slider-thumb]:appearance-none
  [&::-webkit-slider-thumb]:w-4
  [&::-webkit-slider-thumb]:h-4
  [&::-webkit-slider-thumb]:rounded-full
  [&::-webkit-slider-thumb]:bg-white
  [&::-webkit-slider-thumb]:shadow-lg
  [&::-webkit-slider-thumb]:cursor-pointer
  [&::-moz-range-thumb]:w-4
  [&::-moz-range-thumb]:h-4
  [&::-moz-range-thumb]:rounded-full
  [&::-moz-range-thumb]:bg-white
  [&::-moz-range-thumb]:shadow-lg
  [&::-moz-range-thumb]:cursor-pointer
  [&::-moz-range-thumb]:border-none
`;

export function BrandCalculator() {
  const [isAssumptionsExpanded, setIsAssumptionsExpanded] = useState(false);
  
  // Add string state for input values
  const [inputStrings, setInputStrings] = useState({
    tt_minutes: DEFAULT_TT_MIN_PER_DAY.toString(),
    ig_minutes: DEFAULT_IG_MIN_PER_DAY.toString(),
    yt_minutes: DEFAULT_YT_MIN_PER_DAY.toString(),
    tt_conv: (0.080).toString(),  // 0.080%
    ig_conv: (0.050).toString(),  // 0.050%
    yt_conv: (0.030).toString()   // 0.030%
  });

  /**
   * ----------------------------
   * 3) State
   * ----------------------------
   */
  const [inputs, setInputs] = useState<CalculatorInputs>({
    // Platform Minutes
    tt_minutes_per_day: DEFAULT_TT_MIN_PER_DAY,
    ig_minutes_per_day: DEFAULT_IG_MIN_PER_DAY,
    yt_minutes_per_day: DEFAULT_YT_MIN_PER_DAY,

    // Non-Follower Ratios (Pre-Ban)
    tt_non_follower_ratio_pre: 0.90,  // 90% of TikTok content goes to non-followers
    ig_non_follower_ratio_pre: 0.65,  // 65% of IG content goes to non-followers
    yt_non_follower_ratio_pre: 0.75,  // 75% of YT content goes to non-followers

    // Non-Follower Ratios (Post-Ban)
    ig_non_follower_ratio_post: 0.65, // Same as pre-ban by default
    yt_non_follower_ratio_post: 0.75, // Same as pre-ban by default

    // Conversion Rates
    tt_conversion_rate: 0.0008, // 0.080%
    ig_conversion_rate: 0.0005, // 0.050%
    yt_conversion_rate: 0.0005, // 0.030%

    // Spillover & Ads
    ad_siphon: 0.10,           // 10% lost to ads
    ig_share_of_spillover: 0.50, // 50% of TikTok spillover goes to IG
    yt_share_of_spillover: 0.30, // 30% of TikTok spillover goes to YT
    other_share_of_spillover: 0.20 // 20% goes elsewhere
  });

  const [results, setResults] = useState<CalculatorResults | null>(null);

  /**
   * ----------------------------
   * 4) Helper Functions
   * ----------------------------
   */

  // Generic numeric input handler (for conversion rates, etc.)
  const handleInputChange = (field: keyof CalculatorInputs) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseFloat(e.target.value) || 0;
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  /**
   * ----------------------------
   * 4) Core Calculation
   * ----------------------------
   */
  function calculateBanImpact(inputs: CalculatorInputs): CalculatorResults {
    const {
      tt_minutes_per_day,
      ig_minutes_per_day,
      yt_minutes_per_day,
      tt_non_follower_ratio_pre,
      ig_non_follower_ratio_pre,
      yt_non_follower_ratio_pre,
      ig_non_follower_ratio_post,
      yt_non_follower_ratio_post,
      tt_conversion_rate,
      ig_conversion_rate,
      yt_conversion_rate,
      ad_siphon,
      ig_share_of_spillover,
      yt_share_of_spillover
    } = inputs;

    // 1) Pre-Ban Non-Follower Minutes
    const tt_nf_pre = tt_minutes_per_day * tt_non_follower_ratio_pre;
    const ig_nf_pre = ig_minutes_per_day * ig_non_follower_ratio_pre;
    const yt_nf_pre = yt_minutes_per_day * yt_non_follower_ratio_pre;

    // 2) Pre-Ban Revenue
    const preBanRevenue = (
      (tt_nf_pre * tt_conversion_rate)
      + (ig_nf_pre * ig_conversion_rate)
      + (yt_nf_pre * yt_conversion_rate)
    );

    // 3) Sum of Non-Follower Pools (Pre-Ban)
    const preBanNonFollowerTotal = tt_nf_pre + ig_nf_pre + yt_nf_pre;

    // Step B: TikTok Spill-Over (Post-Ban)
    const tt_spillover_total = tt_minutes_per_day * (1 - ad_siphon);

    // IG portion, YT portion
    const ig_spillover_from_tt = tt_spillover_total * ig_share_of_spillover;
    const yt_spillover_from_tt = tt_spillover_total * yt_share_of_spillover;

    // Step C: Post-Ban Totals for IG/YouTube
    const ig_min_per_day_post = ig_minutes_per_day + ig_spillover_from_tt;
    const ig_nf_post = ig_min_per_day_post * ig_non_follower_ratio_post;

    const yt_min_per_day_post = yt_minutes_per_day + yt_spillover_from_tt;
    const yt_nf_post = yt_min_per_day_post * yt_non_follower_ratio_post;

    // Step D: Post-Ban Non-Follower Sum
    const postBanNonFollowerTotal = ig_nf_post + yt_nf_post;

    // Step E: Post-Ban Revenue
    const postBanRevenue = (
      (ig_nf_post * ig_conversion_rate)
      + (yt_nf_post * yt_conversion_rate)
    );

    // Step F: Percentage Changes
    const nonFollowerAttentionChangePct = (preBanNonFollowerTotal === 0)
      ? 0
      : ((postBanNonFollowerTotal - preBanNonFollowerTotal) / preBanNonFollowerTotal) * 100;

    const revenueChangePct = (preBanRevenue === 0)
      ? 0
      : ((postBanRevenue - preBanRevenue) / preBanRevenue) * 100;

    return {
      preBanNonFollowerTotal,
      postBanNonFollowerTotal,
      nonFollowerAttentionChangePct,
      preBanRevenue,
      postBanRevenue,
      revenueChangePct
    };
  }

  /**
   * ----------------------------
   * 5) Core Calculation
   * ----------------------------
   */
  const computeAttention = () => {
    setResults(calculateBanImpact(inputs));
  };

  /**
   * ----------------------------
   * 6) Render the UI
   * ----------------------------
   */
  return (
    <div className={`min-h-screen ${colors.bg} bg-gradient-to-b from-[#0F172A] to-[#1E293B] py-12 px-4 sm:px-6 lg:px-8 font-sans`}>
      <div className="max-w-3xl mx-auto">
        <h1 className={`text-3xl font-bold ${colors.accent} text-center mb-8`}>
          TikTok Ban Impact Calculator
        </h1>

        {/* Model Description */}
        <div className={sectionClass}>
          <h2 className={`text-xl font-semibold ${colors.accent} mb-4`}>
            Model Explanation & Assumptions 🧵
          </h2>
          
          <div className={`${helperTextClass} space-y-3`}>
            <p>
              This is a stock-to-flow model that estimates how a TikTok ban would affect brands using the "Tabs Chocolate" 
              strategy where brands scale new creator accounts horizontally. Success depends heavily on algorithmic distribution to new audiences.
            </p>
            <p>
              Key assumptions:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Brand relies entirely on non-follower traffic and does not run ads</li>
              <li>Creators keep posting daily on each platform (same volume/quality)</li>
              <li>Creators continue on IG/YT but don't explore other platforms</li>
              <li>View conversion rates remain constant after TikTok ban</li>
            </ul>

            <p className="font-medium mt-4">Minutes of attention currently allocated to each platform per day (in billions):</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>TikTok: {inputs.tt_minutes_per_day.toFixed(1)}B</li>
              <li>Instagram Reels: {inputs.ig_minutes_per_day.toFixed(1)}B</li>
              <li>YouTube Shorts: {inputs.yt_minutes_per_day.toFixed(1)}B</li>
            </ul>

            <p className="font-medium mt-4">Percentage of views currently allocated to non-followers per platform:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>TikTok: {(inputs.tt_non_follower_ratio_pre * 100).toFixed(0)}%</li>
              <li>Instagram Reels: {(inputs.ig_non_follower_ratio_pre * 100).toFixed(0)}%</li>
              <li>YouTube Shorts: {(inputs.yt_non_follower_ratio_pre * 100).toFixed(0)}%</li>
            </ul>
            <p>*Note: The numbers above represent my best estimate of what the current numbers should be. If you want to modify them, click the arrow below.</p>
            
            {/* Hidden Inputs Section */}
            <div className={`mt-6 overflow-hidden transition-all duration-300 ease-in-out ${isAssumptionsExpanded ? 'max-h-[800px]' : 'max-h-0'}`}>
            <br/>
              <p className="font-bold mb-4 text-[#F7E8CC]">Modify current attention to each platform:</p>
              <div className="space-y-4 mb-8">
                {/* TikTok Minutes */}
                <div className="flex items-center gap-4">
                  <label className="w-64 text-[#F7E8CC]">TikTok:</label>
                  <input
                    type="text"
                    value={inputStrings.tt_minutes}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setInputStrings(prev => ({ ...prev, tt_minutes: newValue }));
                      const val = parseFloat(newValue);
                      if (!isNaN(val)) {
                        setInputs(prev => ({ ...prev, tt_minutes_per_day: val }));
                      }
                    }}
                    className={inputClass}
                  />
                  <span className="ml-2 text-[#F7E8CC]">billion minutes</span>
                </div>

                {/* Instagram Minutes */}
                <div className="flex items-center gap-4">
                  <label className="w-64 text-[#F7E8CC]">Instagram:</label>
                  <input
                    type="text"
                    value={inputStrings.ig_minutes}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setInputStrings(prev => ({ ...prev, ig_minutes: newValue }));
                      const val = parseFloat(newValue);
                      if (!isNaN(val)) {
                        setInputs(prev => ({ ...prev, ig_minutes_per_day: val }));
                      }
                    }}
                    className={inputClass}
                  />
                  <span className="ml-2 text-[#F7E8CC]">billion minutes</span>
                </div>

                {/* YouTube Minutes */}
                <div className="flex items-center gap-4">
                  <label className="w-64 text-[#F7E8CC]">YouTube:</label>
                  <input
                    type="text"
                    value={inputStrings.yt_minutes}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setInputStrings(prev => ({ ...prev, yt_minutes: newValue }));
                      const val = parseFloat(newValue);
                      if (!isNaN(val)) {
                        setInputs(prev => ({ ...prev, yt_minutes_per_day: val }));
                      }
                    }}
                    className={inputClass}
                  />
                  <span className="ml-2 text-[#F7E8CC]">billion minutes</span>
                </div>
              </div>

              <p className="font-bold mb-4 text-[#F7E8CC]">Modify pre-ban non-follower ratios:</p>
              <div className="space-y-4">
                {/* TikTok Non-Follower Ratio */}
                <div className="flex items-center gap-4">
                  <label className="w-64 text-[#F7E8CC]">TikTok:</label>
                  <input
                    type="range"
                    value={inputs.tt_non_follower_ratio_pre * 100}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) / 100;
                      setInputs(prev => ({ ...prev, tt_non_follower_ratio_pre: val }));
                    }}
                    className={sliderClass}
                    step="1"
                    min="0"
                    max="100"
                  />
                  <div className="w-16 text-[#F7E8CC]">
                    {(inputs.tt_non_follower_ratio_pre * 100).toFixed(0)}%
                  </div>
                </div>

                {/* Instagram Non-Follower Ratio */}
                <div className="flex items-center gap-4">
                  <label className="w-64 text-[#F7E8CC]">Instagram:</label>
                  <input
                    type="range"
                    value={inputs.ig_non_follower_ratio_pre * 100}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) / 100;
                      setInputs(prev => ({ ...prev, ig_non_follower_ratio_pre: val }));
                    }}
                    className={sliderClass}
                    step="1"
                    min="0"
                    max="100"
                  />
                  <div className="w-16 text-[#F7E8CC]">
                    {(inputs.ig_non_follower_ratio_pre * 100).toFixed(0)}%
                  </div>
                </div>

                {/* YouTube Non-Follower Ratio */}
                <div className="flex items-center gap-4">
                  <label className="w-64 text-[#F7E8CC]">YouTube:</label>
                  <input
                    type="range"
                    value={inputs.yt_non_follower_ratio_pre * 100}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) / 100;
                      setInputs(prev => ({ ...prev, yt_non_follower_ratio_pre: val }));
                    }}
                    className={sliderClass}
                    step="1"
                    min="0"
                    max="100"
                  />
                  <div className="w-16 text-[#F7E8CC]">
                    {(inputs.yt_non_follower_ratio_pre * 100).toFixed(0)}%
                  </div>
                </div>
              </div>
            </div>

            {/* Expand/Collapse Arrow */}
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setIsAssumptionsExpanded(!isAssumptionsExpanded)}
                className="focus:outline-none transform transition-transform duration-200"
              >
                <div className={`transform transition-transform duration-200 ${isAssumptionsExpanded ? 'rotate-180' : ''}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#38BDF8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Spillover & Ad Siphon */}
        <div className={sectionClass}>
          <h2 className={`text-xl font-semibold ${colors.accent} mb-6`}>
            1. Attention Shift 🔄
          </h2>
          <p className={helperTextClass + " mb-4"}>
            Where will TikTok's attention go after the ban? Split between IG, YT, and "Other".
            The Ad Siphon % represents attention lost to paid ads. Modify the values below to see how it affects the results.
          </p>
          <div className="space-y-4">
            {/* IG Share */}
            <div className="flex items-center gap-4">
              <label className="w-64 text-[#F7E8CC]">Instagram:</label>
              <input
                type="range"
                value={inputs.ig_share_of_spillover * 100}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) / 100;
                  setInputs(prev => ({ ...prev, ig_share_of_spillover: val }));
                }}
                className={sliderClass}
                step="1"
                min="0"
                max="100"
              />
              <div className="w-16 text-[#F7E8CC]">
                {(inputs.ig_share_of_spillover * 100).toFixed(0)}%
              </div>
            </div>

            {/* YT Share */}
            <div className="flex items-center gap-4">
              <label className="w-64 text-[#F7E8CC]">YouTube:</label>
              <input
                type="range"
                value={inputs.yt_share_of_spillover * 100}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) / 100;
                  setInputs(prev => ({ ...prev, yt_share_of_spillover: val }));
                }}
                className={sliderClass}
                step="1"
                min="0"
                max="100"
              />
              <div className="w-16 text-[#F7E8CC]">
                {(inputs.yt_share_of_spillover * 100).toFixed(0)}%
              </div>
            </div>

            {/* Other Share */}
            <div className="flex items-center gap-4">
              <label className="w-64 text-[#F7E8CC]">Other:</label>
              <input
                type="range"
                value={inputs.other_share_of_spillover * 100}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) / 100;
                  setInputs(prev => ({ ...prev, other_share_of_spillover: val }));
                }}
                className={sliderClass}
                step="1"
                min="0"
                max="100"
              />
              <div className="w-16 text-[#F7E8CC]">
                {(inputs.other_share_of_spillover * 100).toFixed(0)}%
              </div>
            </div>

            {/* Total */}
            <div className="flex items-center gap-4">
              <label className="w-64 text-[#F7E8CC] font-semibold">
                Total:
              </label>
              <div className="w-48" /> {/* Spacer to align with slider width */}
              <div className="w-16 text-[#F7E8CC]">
                <span className={((inputs.ig_share_of_spillover + inputs.yt_share_of_spillover + inputs.other_share_of_spillover) * 100).toFixed(0) === "100"
                  ? "text-green-400"
                  : "text-red-400"}>
                  {((inputs.ig_share_of_spillover + inputs.yt_share_of_spillover + inputs.other_share_of_spillover) * 100).toFixed(0)}%
                </span>
              </div>
            </div>

            {/* Ad Siphon */}
            <div className="pt-4 border-t border-[#F7E8CC]/10">
              <div className="flex items-center gap-4">
                <label className="w-64 text-[#F7E8CC]">Ad Siphon:</label>
                <input
                  type="range"
                  value={inputs.ad_siphon * 100}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) / 100;
                    setInputs(prev => ({ ...prev, ad_siphon: val }));
                  }}
                  className={sliderClass}
                  step="1"
                  min="0"
                  max="100"
                />
                <div className="w-16 text-[#F7E8CC]">
                  {(inputs.ad_siphon * 100).toFixed(0)}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Non-Follower Ratios */}
        <div className={sectionClass}>
          <h2 className={`text-xl font-semibold ${colors.accent} mb-6`}>
            2. Algorithm Boost 📊
          </h2>
          <p className={helperTextClass + " mb-4"}>
            After a TikTok ban, remaining platforms might temporarily boost content distribution to non-followers to attract and retain creators. 
            Modify the values below to see how it affects the results. Defaults are the same as pre-ban values.
          </p>

          <div className="space-y-4">
            {/* Post-Ban Ratios */}
            <div>
              {/* Instagram */}
              <div className="flex items-center gap-4 mb-4">
                <label className="w-64 text-[#F7E8CC]">Instagram:</label>
                <input
                  type="range"
                  value={inputs.ig_non_follower_ratio_post * 100}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) / 100;
                    setInputs(prev => ({ ...prev, ig_non_follower_ratio_post: val }));
                  }}
                  className={sliderClass}
                  step="1"
                  min="0"
                  max="100"
                />
                <div className="w-16 text-[#F7E8CC]">
                  {(inputs.ig_non_follower_ratio_post * 100).toFixed(0)}%
                </div>
              </div>

              {/* YouTube */}
              <div className="flex items-center gap-4">
                <label className="w-64 text-[#F7E8CC]">YouTube:</label>
                <input
                  type="range"
                  value={inputs.yt_non_follower_ratio_post * 100}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) / 100;
                    setInputs(prev => ({ ...prev, yt_non_follower_ratio_post: val }));
                  }}
                  className={sliderClass}
                  step="1"
                  min="0"
                  max="100"
                />
                <div className="w-16 text-[#F7E8CC]">
                  {(inputs.yt_non_follower_ratio_post * 100).toFixed(0)}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Conversion Rates */}
        <div className={sectionClass}>
          <h2 className={`text-xl font-semibold ${colors.accent} mb-6`}>
            3. Conversion Rates 💰
          </h2>
          <p className={helperTextClass + " mb-4"}>
            Platform-specific conversion rates for cold traffic from creator content (as percentages). 
            For example, 0.08% means 8 purchases per 10,000 views. This varies based on organic targeting efficiency.
          </p>
          <p className={helperTextClass + " mb-4"}>
            *Feel free to modify these if you have better data. I have set them to reflect my experience with the platforms.
          </p>
          <div className="space-y-4">
            {/* TikTok Conv */}
            <div className="flex items-center">
              <label className="w-64 text-[#F7E8CC]">TikTok:</label>
              <input
                type="text"
                value={inputStrings.tt_conv}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setInputStrings(prev => ({ ...prev, tt_conv: newValue }));
                  const val = parseFloat(newValue) / 100;
                  if (!isNaN(val)) {
                    setInputs(prev => ({ ...prev, tt_conversion_rate: val }));
                  }
                }}
                className={inputClass}
              />
              <span className="ml-2 text-[#F7E8CC]">%</span>
            </div>

            {/* IG Conv */}
            <div className="flex items-center">
              <label className="w-64 text-[#F7E8CC]">Instagram:</label>
              <input
                type="text"
                value={inputStrings.ig_conv}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setInputStrings(prev => ({ ...prev, ig_conv: newValue }));
                  const val = parseFloat(newValue) / 100;
                  if (!isNaN(val)) {
                    setInputs(prev => ({ ...prev, ig_conversion_rate: val }));
                  }
                }}
                className={inputClass}
              />
              <span className="ml-2 text-[#F7E8CC]">%</span>
            </div>

            {/* YT Conv */}
            <div className="flex items-center">
              <label className="w-64 text-[#F7E8CC]">YouTube:</label>
              <input
                type="text"
                value={inputStrings.yt_conv}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setInputStrings(prev => ({ ...prev, yt_conv: newValue }));
                  const val = parseFloat(newValue) / 100;
                  if (!isNaN(val)) {
                    setInputs(prev => ({ ...prev, yt_conversion_rate: val }));
                  }
                }}
                className={inputClass}
              />
              <span className="ml-2 text-[#F7E8CC]">%</span>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <div className="text-center mb-8">
          <button
            onClick={() => setResults(calculateBanImpact(inputs))}
            className={`
              inline-flex justify-center rounded-lg
              px-6 py-3 text-base font-medium
              bg-gradient-to-r from-[#0EA5E9] to-[#38BDF8]
              text-white shadow-lg
              hover:from-[#0284C7] hover:to-[#0EA5E9]
              focus:outline-none focus:ring-2
              focus:ring-[#38BDF8] focus:ring-offset-2
              focus:ring-offset-[#0F172A]
              transition-all duration-200
            `}
          >
            Calculate Impact
          </button>
        </div>

        {/* Results */}
        {results && (
          <div className={sectionClass}>
            <h2 className={`text-xl font-semibold ${colors.accent} mb-6`}>
              Impact Summary
            </h2>
            
            <div className="p-6 rounded-lg bg-[#1a2234] border border-[#334155]">
              <div className="space-y-6">
                {/* Non-Follower Attention Change */}
                <div className="flex justify-between items-center">
                  <span className="text-[#94A3B8] font-medium">Organic Reach Impact <br/>(Non-Follower):</span>
                  <span className={`text-2xl font-bold ${results.nonFollowerAttentionChangePct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {results.nonFollowerAttentionChangePct > 0 ? '+' : ''}{results.nonFollowerAttentionChangePct.toFixed(1)}%
                  </span>
                </div>

                {/* Revenue Impact */}
                <div className="pt-6 border-t border-[#334155] flex justify-between items-center">
                  <span className="text-[#94A3B8] font-medium">Expected Revenue Impact:</span>
                  <span className={`text-2xl font-bold ${results.revenueChangePct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {results.revenueChangePct > 0 ? '+' : ''}{results.revenueChangePct.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-12 pb-8 text-center text-sm text-gray-400">
        If you find this useful, follow me on twitter <a href="https://twitter.com/iamrobertopablo" target="_blank" rel="noopener noreferrer" className="text-[#38BDF8]">@iamrobertopablo</a>. I will be making a similar model for ad inventory next.
      </div>
    </div>
  );
}
